import React, { HTMLProps, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Goal, GoalPage } from '../../../../config/goals';
import { GoalQuestionCard } from '../goal-question-card/GoalQuestionCard';
import Slider from 'rc-slider';
import { GoalQuestionBase } from './GoalQuestionBase';
import { GoalIndicationContext } from '../../../../contexts/investment-robot/GoalIndicationContext';
import CurrencyInput from 'react-currency-input-field';
import { CurrencyInputProps } from 'react-currency-input-field/dist/components/CurrencyInputProps';
import { toPrice, reverseFormatNumber } from '../../../../utils/parsers';
import Toggle from 'react-toggle';
import { ForecastContext } from '../../../../contexts/investment-robot/ForecastContext';
import { Pill } from '../../../general/Pill';
import { RISK_PROFILE_FLUCTUATIONS } from '../../../../config/risk-profiles';
import { IconElements } from '../../../../config/icons';
import { useQuery } from 'react-query';
import { postRetirementTargetCapital, RiskProfile } from '../../../../api/robotapi';
import { LoadingStateWrapper } from '../../../general/LoadingStateWrapper';
import { PathPageIcon } from '../../../../config/iconSelector';
import { Trans, useTranslation } from 'react-i18next';
import { toPercentage } from '../../forecast-screen/ForecastResultsCalculations';
import { ForecastEmailMeInputs } from '../../forecast-screen/email-me-modal/ForecastEmailMeCard';
import { RobotEnvironmentContext } from '../../../../contexts/general/RobotEnvironmentContext';
import { getGeneralPages, getGoalSpecificPages } from '../../../../utils/functions';
import { ForecastSummaryData } from '../../forecast-screen/ForecastSummaryData';

export const GoalQuestion: React.FC<{
    goal: Goal;
    goalPage: GoalPage;
    goalPageIndex: number;
    children?: React.ReactNode;
}> = ({ goal, goalPage: page, goalPageIndex, children }) => {
    const history = useHistory();
    const { parameters, actions } = useContext(GoalIndicationContext);
    const robotEnvironmentContext = useContext(RobotEnvironmentContext);
    const { t } = useTranslation('investment');
    const [error, setError] = useState<string | null>(null);

    const value = page.value.get(parameters);

    const isNextDisabled =
        page.value.defaultValueOnEmpty === undefined && (value === undefined || page.value.validate(value) !== null);

    const getTotalPages = () => {
        const goalSpecificPages = getGoalSpecificPages(goal.pages, robotEnvironmentContext.contactDetailsOption);
        const generalPages = getGeneralPages(robotEnvironmentContext.contactDetailsOption);
        return goalSpecificPages.length + generalPages.length + 1; // + 1 to indicate there is always 1 extra step
    };

    return (
        <GoalQuestionBase
            header={{
                title: goal.label,
            }}
            buttons={{
                next: {
                    id: 'next',
                    label: t('general-next'),
                    onClick: () => {
                        value === undefined &&
                            page.value.defaultValueOnEmpty &&
                            actions.setParameters(page.value.onChange(page.value.defaultValueOnEmpty?.(parameters)));
                        history.push(page.routes.next);
                    },
                    isDisabled: isNextDisabled,
                },
                previous: {
                    id: 'back',
                    label: t('general-previous'),
                    onClick: () => {
                        history.goBack();
                    },
                },
            }}
            pageNumberIndicators={{
                current: goalPageIndex + 1,
                total: getTotalPages(),
            }}
        >
            <GoalQuestionCard
                question={page.question.label(parameters)}
                description={page.question.description?.(parameters)}
                icon={PathPageIcon(page.pageId, 'w-12 h-12 text-bnd-green')}
                tooltip={page.question.tooltip}
            >
                {page.question.type === 'money' && (
                    <QuestionMoneyInput
                        allowNegativeValue={false}
                        type="text"
                        placeholder={page.value.placeholder || '€20.000'}
                        className={`py-2 px-4 border border-gray-200 rounded-lg focus:outline-hidden ${
                            error ? 'border border-red-300' : ''
                        }`}
                        fixedDecimalLength={0}
                        decimalsLimit={2}
                        intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                        allowDecimals={false}
                        value={value ? +value / 100 : undefined}
                        onValueChange={(_value = '') => {
                            const value = reverseFormatNumber(_value, 'nl-NL') * 100;
                            setError(page.value.validate(value + ''));
                            actions.setParameters(page.value.onChange(value + ''));
                        }}
                    />
                )}
                {page.question.type === 'quantity' && <QuestionQuantityInput page={page} />}
                {page.question.type === 'date' && (
                    <input
                        className="focus:outline-none font-medium"
                        type="text"
                        inputMode="numeric"
                        placeholder="Datum"
                        onChange={(e) => {
                            const error = page.value.validate(e.target.value);
                            error === null
                                ? actions.setParameters(page.value.onChange(e.target.value))
                                : setError(error);
                        }}
                    />
                )}
                {page.question.type === 'select' && <QuestionSelectInput page={page} />}
                {page.question.type === 'switch' && (
                    <Toggle
                        className="mt-1"
                        icons={false}
                        checked={parameters.risk?.reduction?.isEnabled}
                        onChange={(event) => {
                            actions.setParameters({
                                risk: {
                                    reduction: {
                                        isEnabled: event.target.checked,
                                    },
                                },
                            });
                        }}
                    />
                )}
                {page.question.type === 'riskProfile' && <QuestionRiskProfileInput page={page} />}
                {page.question.type === 'pensionPeriodicIncome' && <QuestionPensionInput page={page} />}
                {page.question.type === 'pensionProposedCapitalGoal' && (
                    <QuestionPensionProposedCapitalGoalInput page={page} />
                )}
                {page.question.type === 'contactDetails' && <ForecastEmailMeInputs />}
            </GoalQuestionCard>
        </GoalQuestionBase>
    );
};

// From proposal in decimals (14235,99) to rounded to thousands in cents (14000_00)
const ceiledPensionProposal = (proposal: number): number => {
    return Math.ceil(proposal / 1000) * 1000 * 100;
};

const QuestionPensionProposedCapitalGoalInput: React.FC<{ page: GoalPage } & HTMLProps<HTMLDivElement>> = ({
    page,
}) => {
    const goalIndicationContext = useContext(GoalIndicationContext);

    const { t } = useTranslation('investment');

    const queryCacheParams = {
        amountPerInterval: goalIndicationContext.parameters.goals?.pension?.capitalGoal?.amountPerInterval,
        retirementPeriodInYears: goalIndicationContext.parameters.goals?.pension?.capitalGoal?.amountPerInterval,
        interval: goalIndicationContext.parameters.goals?.pension?.capitalGoal?.interval,
    };

    const capitalGoalAmountQuery = useQuery(['retirementCapitalGoal', queryCacheParams], () =>
        postRetirementTargetCapital(goalIndicationContext.parameters),
    );

    useEffect(
        () => {
            if (!capitalGoalAmountQuery.data) return;
            goalIndicationContext.actions.setParameters({
                goals: {
                    pension: {
                        capitalGoal: {
                            customAmount: ceiledPensionProposal(capitalGoalAmountQuery.data?.targetCapital),
                        },
                    },
                },
            });
        } /* eslint-disable */,
        [
            capitalGoalAmountQuery.data?.targetCapital,
            goalIndicationContext.parameters.goals?.pension?.capitalGoal?.interval,
        ],
    ); /* eslint-enable */

    const amountPerInterval = goalIndicationContext.parameters.goals?.pension?.capitalGoal?.amountPerInterval;
    const retirementPeriodInYears =
        goalIndicationContext.parameters.goals?.pension?.capitalGoal?.retirementPeriodInYears;
    const interval = goalIndicationContext.parameters.goals?.pension?.capitalGoal?.interval;

    const customAmount = goalIndicationContext.parameters.goals?.pension?.capitalGoal?.customAmount;

    if (
        amountPerInterval === undefined ||
        retirementPeriodInYears === undefined ||
        retirementPeriodInYears === 0 ||
        interval === undefined
    ) {
        return <p>{t('goal-indication-screen-pension-missing-parameters')}</p>;
    }

    return (
        <LoadingStateWrapper
            isLoading={capitalGoalAmountQuery.isLoading}
            isFetching={capitalGoalAmountQuery.isFetching}
            isIdle={capitalGoalAmountQuery.isIdle}
            isError={capitalGoalAmountQuery.isError}
            errorElement={
                <div>
                    <p className="">
                        {t('goal-indication-screen-pension-could-not-calculate', {
                            amount: toPrice(+amountPerInterval, { onlyFullEuros: true, withCurrencySymbol: true }),
                            interval,
                            retirementPeriodInYears,
                        })}
                    </p>
                    <CurrencyInput
                        allowNegativeValue={false}
                        type="text"
                        placeholder={page.value.placeholder || '€20.000'}
                        className={`mt-8 py-2 px-4 w-full border border-gray-200 rounded-lg font-medium`}
                        fixedDecimalLength={0}
                        decimalsLimit={2}
                        intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                        allowDecimals={false}
                        value={customAmount ? +customAmount / 100 : undefined}
                        onValueChange={(_value = '') => {
                            let value = reverseFormatNumber(_value, 'nl-NL') * 100;
                            value = value < 0 ? 0 : value;
                            goalIndicationContext.actions.setParameters({
                                goals: { pension: { capitalGoal: { customAmount: value } } },
                            });
                        }}
                    />
                </div>
            }
        >
            <div>
                <p className="">
                    {t('goal-indication-screen-pension-calculation', {
                        amount: toPrice(+amountPerInterval, { onlyFullEuros: true, withCurrencySymbol: true }),
                        retirementPeriodInYears,
                        calculationResult: toPrice(ceiledPensionProposal(capitalGoalAmountQuery.data?.targetCapital!), {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        }),
                        interval: interval === 'monthly' ? 'maand' : 'jaar',
                    })}
                </p>
                <CurrencyInput
                    allowNegativeValue={false}
                    type="text"
                    placeholder={page.value.placeholder || '€20.000'}
                    className={`mt-8 py-2 px-4 w-full border border-gray-200 rounded-lg font-medium`}
                    fixedDecimalLength={0}
                    decimalsLimit={2}
                    intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                    allowDecimals={false}
                    value={customAmount ? +customAmount / 100 : undefined}
                    onValueChange={(_value = '') => {
                        const value = reverseFormatNumber(_value, 'nl-NL') * 100;
                        goalIndicationContext.actions.setParameters({
                            goals: { pension: { capitalGoal: { customAmount: value } } },
                        });
                    }}
                />
            </div>
        </LoadingStateWrapper>
    );
};

const QuestionPensionInput: React.FC<{ page: GoalPage } & HTMLProps<HTMLDivElement>> = ({ page }) => {
    const goalIndicationContext = useContext(GoalIndicationContext);
    const { t } = useTranslation('investment');

    const amountPerInterval = goalIndicationContext.parameters.goals?.pension?.capitalGoal?.amountPerInterval;
    const retirementPeriodInYears =
        goalIndicationContext.parameters.goals?.pension?.capitalGoal?.retirementPeriodInYears || '';
    const interval = goalIndicationContext.parameters.goals?.pension?.capitalGoal?.interval;

    const [error] = useState<string | null>(null);

    return (
        <div>
            {t('goal-indication-screen-pension-i-want')}{' '}
            <input
                className="m-1 px-4 py-2 border border-gray-200 rounded-lg text-center font-medium"
                style={{ minWidth: '50px', maxWidth: '60px' }}
                type="numeric"
                min="1"
                max="50"
                placeholder="20"
                value={retirementPeriodInYears}
                onChange={(e) => {
                    goalIndicationContext.actions.setParameters({
                        goals: {
                            pension: {
                                capitalGoal: {
                                    retirementPeriodInYears: +e.target.value,
                                },
                            },
                        },
                    });
                }}
            />{' '}
            {t('goal-indication-screen-pension-year-long')}{' '}
            <CurrencyInput
                allowNegativeValue={false}
                type="text"
                placeholder="€1.500"
                className={`m-1 py-2 px-4 w-28 border border-gray-200 rounded-lg font-medium ${
                    error ? 'border border-red-300' : ''
                }`}
                fixedDecimalLength={0}
                decimalsLimit={2}
                intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                allowDecimals={false}
                value={amountPerInterval ? +amountPerInterval / 100 : undefined}
                onValueChange={(_value = '') => {
                    const value = reverseFormatNumber(_value, 'nl-NL') * 100;
                    goalIndicationContext.actions.setParameters({
                        goals: { pension: { capitalGoal: { amountPerInterval: value } } },
                    });
                }}
            />{' '}
            per{' '}
            <select
                className="m-1 p-2 border border-gray-200 rounded-lg bg-transparent font-medium"
                value={interval}
                onChange={(e) => {
                    goalIndicationContext.actions.setParameters({
                        goals: { pension: { capitalGoal: { interval: e.target.value as 'yearly' | 'monthly' } } },
                    });
                }}
            >
                <option value="monthly">maand</option>
                <option value="yearly">jaar</option>
            </select>{' '}
            {t('goal-indication-screen-pension-extra-income')}.
        </div>
    );
};

const QuestionSelectInput: React.FC<{ page: GoalPage } & HTMLProps<HTMLDivElement>> = ({ page }) => {
    const { parameters, actions } = useContext(GoalIndicationContext);
    const value = page.value.get(parameters) || 'none';

    return (
        <>
            <span className={`relative flex flex-row items-center px-4 py-0.5 border rounded-lg`}>
                <select
                    //className="p-3 border border-gray-300 rounded-lg"
                    className="w-full px-0 py-1.5 pr-10 focus:outline-hidden appearance-none bg-opacity-0 cursor-pointer font-medium"
                    value={value}
                    onChange={(e) => {
                        actions.setParameters(page.value.onChange(e.target.value));
                    }}
                >
                    {value === 'none' && (
                        <option value={'none'} key={'none'}>
                            {page.question.select?.placeholder || 'Geen selectie'}
                        </option>
                    )}
                    {page.question.select?.options?.map((option) => {
                        return (
                            <option value={option.value} key={option.value + page.pageId}>
                                {option.label}
                            </option>
                        );
                    })}
                </select>
                <IconElements.ChevronDown className="absolute right-4 h-4 w-4 text-black pointer-events-none" />
            </span>
        </>
    );
};

const QuestionQuantityInput: React.FC<{ page: GoalPage } & HTMLProps<HTMLDivElement>> = ({ page }) => {
    const { parameters, actions } = useContext(GoalIndicationContext);

    const value = page.value.get(parameters);
    const min = page.value.rules?.min || page.value.rules?.minFn?.(parameters) || 0;

    useEffect(() => {
        if (value === undefined) {
            actions.setParameters(page.value.onChange(min + ''));
        } else if (+value < min) {
            actions.setParameters(page.value.onChange(min + ''));
        }
    }, [value, min]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            <div className="mb-4">
                {page.value.sliderPreviewText?.(
                    value ||
                        (page.value.rules?.min ? page.value.rules?.min + '' : undefined) ||
                        (page.value.rules?.minFn?.(parameters)
                            ? page.value.rules?.minFn?.(parameters) + ''
                            : undefined) ||
                        1 + '',
                ) ||
                    +page.value.get(parameters)! ||
                    0}
            </div>
            <Slider
                value={
                    page.value.get(parameters)
                        ? +page.value.get(parameters)!
                        : page.value.rules?.min || page.value.rules?.minFn?.(parameters) || 0
                }
                min={page.value.rules?.min || page.value.rules?.minFn?.(parameters) || 0}
                max={page.value.rules?.max || page.value.rules?.maxFn?.(parameters) || 100}
                step={page.value.rules?.step}
                onChange={(value) => {
                    actions.setParameters(page.value.onChange(value + ''));
                }}
                styles={{
                    handle: {
                        border: 'none',
                    },
                }}
            />
        </div>
    );
};

const QuestionMoneyInput: React.FC<CurrencyInputProps> = ({ ...rest }) => {
    return <CurrencyInput className={'font-medium'} {...rest} />;
};

const getRatio = (selectedProfile: RiskProfile) => {
    const sumShare = selectedProfile.funds
        .filter((f) => f.category === 'Stock')
        .reduce((total, fund) => total + fund.weight, 0);

    const sumObligate = selectedProfile.funds
        .filter((f) => f.category === 'Bond')
        .reduce((total, fund) => total + fund.weight, 0);

    return { bondTotal: toPercentage(sumObligate), shareTotal: toPercentage(sumShare) };
};

const QuestionRiskProfileInput: React.FC<{ page: GoalPage } & HTMLProps<HTMLDivElement>> = ({ page }) => {
    const forecastContext = useContext(ForecastContext);
    const goalIndicationContext = useContext(GoalIndicationContext);

    const { t } = useTranslation('investment');

    useEffect(() => {
        goalIndicationContext.actions.propagate();
    }, [goalIndicationContext.parameters.risk?.selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

    const { forecast, riskProfiles } = forecastContext;

    const selectedProfileId = page.value.get(goalIndicationContext.parameters);
    const selectedDescription = page.question.riskProfile?.descriptions.find((d) => d.id === selectedProfileId);
    const selectedProfile = riskProfiles.query.data?.data.find((r) => r.id + '' === selectedProfileId);
    const selectedProfileFluctuations = RISK_PROFILE_FLUCTUATIONS.find((p) => p.id === selectedProfile?.id + '');

    const ratios = useMemo(() => {
        return riskProfiles.query.data?.data.map((p) => {
            return {
                id: p.id,
                ...getRatio(p),
            };
        });
    }, [riskProfiles.query.data]);
    return (
        <LoadingStateWrapper
            isLoading={riskProfiles.query.isLoading}
            isError={riskProfiles.query.isError}
            isFetching={riskProfiles.query.isFetching}
            isIdle={riskProfiles.query.isIdle}
        >
            <div className="grid grid-cols-1">
                <div className="flex flex-col">
                    <div className="flex-1 flex flex-row flex-start whitespace-nowrap overflow-y-hidden -mx-8 md:-mx-12">
                        <div className="flex flex-row mx-auto px-12">
                            {riskProfiles.query.data?.data.map((profile, i) => {
                                return (
                                    <Pill
                                        tint="green"
                                        onClick={() => {
                                            goalIndicationContext.actions.setParameters(
                                                page.value.onChange(profile.id + ''),
                                            );
                                        }}
                                        isActive={selectedProfileId === profile.id + ''}
                                        className={`${i > 0 && 'ml-4'}`}
                                        key={'stratpill' + profile.id}
                                    >
                                        {profile.description}
                                    </Pill>
                                );
                            })}
                        </div>
                    </div>
                    <span className="flex sm:hidden justify-center">
                        <IconElements.DoubleEndedArrow className="w-10 h-10 text-bnd-green" />
                    </span>
                </div>
                {selectedProfile && (
                    <>
                        <div className="font-medium mt-8">
                            {t('goal-indication-screen-risk-profiles-description-additional-title')}
                        </div>
                        <p className="block font-base">
                            <Trans
                                t={t}
                                i18nKey={'forecast-screen-calculation-configuration-risk-profile-description'}
                                values={{
                                    riskProfile: selectedProfile.description.toLowerCase(),
                                    optimisticFluctuation: selectedProfileFluctuations?.optimistic,
                                    pessimisticFluctuation: selectedProfileFluctuations?.pessimistic,
                                    suffix:
                                        selectedProfile.description === 'Zeer defensief'
                                            ? ''
                                            : t(
                                                  'forecast-screen-calculation-configuration-risk-profile-description-suffix',
                                              ),
                                }}
                            >
                                ...
                                <span className="font-medium">riskProfile</span>
                                <span className="font-medium">optimisticFluctuation</span>
                                <span className="font-medium">pessimisticFluctuation</span>
                                ...
                            </Trans>
                        </p>
                        <div className="mt-2">
                            <ForecastSummaryData
                                className="mb-4"
                                classNameTitle="mt-4 font-medium"
                                classNameText="w-64"
                                forecast={forecast.query.data}
                                forecastParameters={forecast.parameters}
                                isLoading={forecast.query.isLoading}
                                isError={forecast.query.isError}
                                isFetching={forecast.query.isFetching}
                                isIdle={forecast.query.isIdle}
                            />
                        </div>
                    </>
                )}
                {selectedDescription === undefined && (
                    <div className="mt-12">
                        {riskProfiles.query.data?.data.map((profile, i) => {
                            const ratio = ratios?.find((r) => r.id === profile.id);
                            if (ratio === undefined) return <>geen ratio</>;
                            return (
                                <p className="mt-1 text-center text-gray-400 text-sm" key={profile.id}>
                                    {profile.description} = {ratio.shareTotal}% aandelen, {ratio.bondTotal}% obligaties
                                </p>
                            );
                        })}
                    </div>
                )}
            </div>
        </LoadingStateWrapper>
    );
};
